import React, { useState, useEffect } from 'react';
import useGameBase from '../../hooks/useGameBase';

export default function StatClashGame({ roomId, gameState, gamePlayers = [], user = {}, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, roomId });
  const socketId = user?.socketId;
  const localDeck = (gameState && gameState.playerDecks && socketId ? gameState.playerDecks[socketId] : []) || [];
  const topCard = localDeck && localDeck.length > 0 ? localDeck[0] : null;
  // whether this client is the current chooser (allowed to pick a stat)
  const isChooser = !!(socketId && gameState && gameState.currentPlayer && socketId === gameState.currentPlayer);

  const [choosing, setChoosing] = useState(false);
  // Reveal is now driven by server state: gameState.revealStage and gameState.showWinningState
  const [descVisible, setDescVisible] = useState(false);
  // index of how many other players have been revealed (0-based). -1 means none yet.
  const [revealIndex, setRevealIndex] = useState(-1);

  // derive reveal state from server-driven gameState
  const serverRevealStage = gameState && typeof gameState.revealStage === 'number' ? gameState.revealStage : 0;
  const revealing = serverRevealStage > 0;

  // When server enters reveal staging (stage 2), advance revealIndex over the other players one at a time.
  useEffect(() => {
    const lr = gameState && gameState.lastRound;
    if (!lr) {
      setRevealIndex(-1);
      return undefined;
    }

    // build ordered players to determine how many 'other' players exist
    const perPlayer = Array.isArray(lr.perPlayer) ? lr.perPlayer.slice() : [];
    const perMap = Object.fromEntries((perPlayer || []).map((p) => [p.socketId, p]));
    const ordered = (gamePlayers || []).map((gp) => {
      const sid = gp.socketId || gp.id;
      return perMap[sid] ? perMap[sid] : { socketId: sid, card: null, value: null };
    });
    const chooserId = lr.chosenBy || lr.chosen || null;
    const remaining = ordered.filter((p) => !chooserId || p.socketId !== chooserId);
    const total = remaining.length;

    let timer = null;
    if (serverRevealStage >= 2 && serverRevealStage < 3 && total > 0) {
      // start revealing one-by-one
      setRevealIndex(0);
      timer = setInterval(() => {
        setRevealIndex((i) => {
          if (i >= total - 1) {
            // reached the end; stop incrementing
            clearInterval(timer);
            return i;
          }
          return i + 1;
        });
      }, 1900);
    } else if (serverRevealStage < 2) {
      setRevealIndex(-1);
    } else if (serverRevealStage >= 3) {
      // fully revealed
      setRevealIndex(total - 1);
    }

    return () => { if (timer) clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverRevealStage, gameState && gameState.lastRound && gameState.lastRound.perPlayer && gameState.lastRound.perPlayer.length, gamePlayers.length]);

  const chooseStat = async (stat) => {
    if (!stat) return;
    setChoosing(true);
    try {
      const action = { type: 'chooseStat', stat };
      if (typeof onGameAction === 'function') onGameAction(action);
      else await submitAction(action);
    } catch (e) {
      console.error('chooseStat failed', e);
    } finally {
      setChoosing(false);
    }
  };

  // Resolve attribute metadata from pack metadata (if present).
  const getAttributeMeta = (statKey) => {
    const meta = gameState && gameState.packMeta ? gameState.packMeta : null;
    if (!meta) return { rule: 'higher' };
    const attrs = meta.attributes || meta.Attributes || null;
    if (!attrs) return { rule: 'higher' };
    // If attributes is an object mapping name->rule
    if (typeof attrs === 'object' && !Array.isArray(attrs)) {
      const v = attrs[statKey] || attrs[statKey.toLowerCase()] || attrs[statKey.toUpperCase()];
      if (v) return { rule: String(v).toLowerCase() };
    }
    // If attributes is an array of objects
    if (Array.isArray(attrs)) {
      const found = attrs.find((a) => {
        if (!a) return false;
        const n = a.name || a.Name || a.key || a.attribute || a.label || '';
        return typeof n === 'string' && n.toLowerCase() === String(statKey).toLowerCase();
      });
      if (found) {
        const rule = (found.order || found.compare || found.rule || found.better || found.prefers || found.wins || found.direction || found.when) || found.type || 'higher';
        return { rule: String(rule).toLowerCase(), meta: found };
      }
    }
    return { rule: 'higher' };
  };

  // Create a sanitized lowercase filename from a title
  const safeFileName = (title) => {
    if (!title) return 'card';
    return String(title).toLowerCase()
      .replace(/[^a-z0-9 _-]/g, '')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .trim();
  };

  const renderTopCard = () => {
    const lr = gameState && gameState.lastRound;
    // If a lastRound exists and we're in revealing mode, don't show the playable top card
    if (lr && revealing) return null;
    if (!topCard) return <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded">No cards left</div>;
    return (
      <div className="p-4 bg-white dark:bg-gray-700 rounded shadow-md w-72 text-gray-900 dark:text-gray-100">
        <div className="mb-2 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="font-bold text-lg">{topCard.title}</div>
            <button
              onClick={() => setDescVisible((v) => !v)}
              aria-pressed={!descVisible}
              title={descVisible ? 'Hide description' : 'Show description'}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 transition-colors"
            >
              {descVisible ? 'Info' : 'Info'}
            </button>
          </div>
          <div className={`text-xs text-gray-500 dark:text-gray-400 transform transition-all duration-300 ease-in-out ${descVisible ? 'opacity-100 mt-2 max-h-80 overflow-hidden' : 'opacity-0 max-h-0 mt-0 overflow-hidden'}`}>
            {topCard.description || topCard.funFact || ''}
          </div>
        </div>

        <div className="w-full h-40 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden mb-3">
          {(() => {
            const img = topCard.image || null;
            const packName = (gameState && gameState.pack) ? gameState.pack : 'cars';
            let src = null;
            if (img) {
              const isUrl = String(img).startsWith('/') || String(img).startsWith('http');
              src = isUrl ? img : (packName ? `/images/statclash/${packName}/${img}` : img);
            } else if (topCard && topCard.title) {
              const fname = safeFileName(topCard.title) + '.jpg';
              src = `/images/statclash/${packName}/${fname}`;
            }
            if (src) {
              return (
                <img
                  src={src}
                  alt={topCard.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/images/statclash/placeholder.jpg'; }}
                />
              );
            }
            return <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">No image</div>;
          })()}
        </div>

        <div className="grid grid-cols-1 gap-2">
          {Object.entries(topCard.stats).map(([k, v]) => {
            const attr = getAttributeMeta(k);
            const rule = (attr && attr.rule) ? String(attr.rule).toLowerCase() : 'higher';
            // choose icon and title
            let icon = '↑';
            let hint = 'Higher wins';
            let iconClass = 'text-green-300';
            if (rule.includes('low') || rule.includes('lower') || rule.includes('min')) {
              icon = '↓'; hint = 'Lower wins'; iconClass = 'text-red-300';
            } else if (rule.includes('earl') || rule.includes('early') || rule.includes('first')) {
              icon = '⏱'; hint = 'Earliest wins'; iconClass = 'text-yellow-300';
            }
            return (
              <button
                  key={k}
                  onClick={() => { if (isChooser && !choosing) chooseStat(k); }}
                  disabled={!isChooser || choosing}
                  className={`w-full text-left flex justify-between items-center text-sm p-2 rounded transition-colors ${(!isChooser || choosing) ? 'opacity-60 cursor-not-allowed bg-gray-900 dark:bg-gray-900  text-gray-200 dark:text-gray-200' : 'bg-gray-900 dark:bg-gray-900 text-gray-200 dark:text-gray-200 hover:bg-blue-700 hover:dark:bg-blue-700 hover:text-white'}`}
                  aria-pressed={String(!(!isChooser || choosing))}
                  title={!isChooser ? 'Only the current player may choose a stat' : hint}
                >
                <div className="flex items-center gap-2">
                  <span className={`text-s font-bold ${iconClass}`}>{icon}</span>
                  <div className="font-medium">{k}</div>
                </div>
                <div className="font-mono">{v}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPot = () => {
    const pot = (gameState && gameState.pot) || [];
    if (!pot || pot.length === 0) return null;
  // don't show the pot while staged reveal is in progress until values are visible
  if (serverRevealStage >= 1 && serverRevealStage < 2) return null;
    return (
      <div className="mt-4 p-2 bg-orange-300 dark:bg-orange-900 rounded w-72 text-gray-900 dark:text-gray-100">
        <div className="font-semibold">Pot ({pot.length} cards)</div>
      </div>
    );
  };

  const renderLastRound = () => {
  const lr = gameState && gameState.lastRound;
    if (!lr) return null;
    // Show reveal panel: list each player and their revealed value (or No card)
    const perPlayer = Array.isArray(lr.perPlayer) ? lr.perPlayer.slice() : [];
    const winners = lr.winners || (lr.winner ? [lr.winner] : []);
    const chooserId = lr.chosenBy || lr.chosen || null;
    // Build a map for quick lookup of reported perPlayer entries
    const perMap = Object.fromEntries((perPlayer || []).map((p) => [p.socketId, p]));
    // Use gamePlayers ordering as a fallback to render other players consistently
    const ordered = (gamePlayers || []).map((gp) => {
      const sid = gp.socketId || gp.id;
      return perMap[sid] ? perMap[sid] : { socketId: sid, card: null, value: null };
    });
    const chooser = chooserId ? (perMap[chooserId] || ordered.find((p) => p.socketId === chooserId) || null) : null;
    // determine rule for ordering
    const attrMeta = getAttributeMeta(lr.stat || '');
    const rule = (attrMeta && attrMeta.rule) ? String(attrMeta.rule).toLowerCase() : 'higher';
    // prepare sorted remaining players (exclude chooser)
  const remaining = ordered.filter((p) => !chooser || p.socketId !== chooserId);
    // Parse numeric or date-like values. For dates: remove commas, detect 'BC' and negate the year (e.g. '30,000 BC' -> -30000)
    const parseDateOrNumber = (raw) => {
      if (raw === undefined || raw === null) return NaN;
      if (typeof raw === 'number') return raw;
      let s = String(raw).trim();
      if (!s) return NaN;
      const isBC = /\bBC\b/i.test(s);
      // remove commas
      s = s.replace(/,/g, '');
      // attempt to find first integer in the string
      const m = s.match(/-?\d+/);
      if (!m) return NaN;
      let n = Number(m[0]);
      if (isBC) n = -Math.abs(n);
      return Number.isNaN(n) ? NaN : n;
    };

    const numericFor = (p) => {
      if (!p) return NaN;
      // prefer an explicit revealed value, otherwise fall back to the card stat
      const v = (p.value !== undefined && p.value !== null) ? p.value : (p.card && p.card.stats ? p.card.stats[lr.stat] : null);
      return parseDateOrNumber(v);
    };
    const sortedRemaining = remaining.slice().sort((a, b) => {
      const na = numericFor(a);
      const nb = numericFor(b);
      // put NaNs at end
      if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
      if (Number.isNaN(na)) return 1;
      if (Number.isNaN(nb)) return -1;
      if (rule.includes('low') || rule.includes('min') || rule.includes('lower') || rule.includes('least') || rule.includes('earl') || rule.includes('early') || rule.includes('first')) {
        return na - nb; // ascending
      }
      return nb - na; // descending for higher-wins
    });
  // Only show winner highlights once the values stage has been revealed
  const showWinners = serverRevealStage >= 2;

  return serverRevealStage >= 1 ? (
        <div className="mt-4 w-full max-w-4xl p-3 bg-white dark:bg-gray-800 rounded shadow-sm">
              <div className="grid gap-3">
      
          {/* 1) Show chooser first */}
          {serverRevealStage >= 1 && chooser ? (
              <div>
                <div className="font-semibold mb-2 text-gray-900 dark:text-gray-200">Results: {lr.stat}</div>
                {/* chooser row: same format as the ordered player rows (name left, value right) */}
                {(() => {
                  const p = gamePlayers.find(gp => (gp.socketId||gp.id) === chooserId) || { username: chooserId };
                  const val = (chooser.value !== undefined && chooser.value !== null) ? chooser.value : (chooser.card && chooser.card.stats ? chooser.card.stats[lr.stat] : null);
                  const isWinner = showWinners && winners.includes(chooserId);
                  return (
                    <div className={`flex justify-between items-center px-2 py-1 rounded ${isWinner ? 'bg-green-200 dark:bg-green-700 font-semibold text-gray-900 dark:text-gray-200' : 'bg-transparent text-gray-500 dark:text-gray-300'}`}>
                      <div className="text-sm">{p.username} <span className="text-xs ">(chooser)</span></div>
                      <div className="font-mono">{val ?? '—'}</div>
                    </div>
                  );
                })()}
              </div>
            ) : null}

          {/* 2) Show other players' values in order */}
          {serverRevealStage >= 2 && (
            <div>
              <div className="space-y-1">
          {(() => {
            if (revealIndex < 0) return null;
            const toShow = sortedRemaining.slice(0, revealIndex + 1);
            return toShow.map((p) => {
              const player = gamePlayers.find(gp => (gp.socketId||gp.id) === p.socketId) || { username: p.socketId };
              const val = (p.value !== undefined && p.value !== null) ? p.value : (p.card && p.card.stats ? p.card.stats[lr.stat] : null);
              const isWinner = showWinners && winners.includes(p.socketId);
              return (
                <div key={p.socketId} className={`flex justify-between items-center px-2 py-1 rounded ${isWinner ? 'bg-green-200 dark:bg-green-700 font-semibold text-gray-900 dark:text-gray-200' : 'bg-transparent text-gray-900 dark:text-gray-200'}`}>
                  <div className="text-sm">{player.username}</div>
                  <div className="font-mono">{val ?? '—'}</div>
                </div>
              );
            });
          })()}
              </div>
            </div>
          )}

          {/* 3) Once staging complete, show full cards grid */}
          {serverRevealStage >= 3 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {perPlayer.map((p) => {
                const player = gamePlayers.find(gp => (gp.socketId||gp.id) === p.socketId) || { username: p.socketId };
                const isWinner = showWinners && winners.includes(p.socketId);
                const card = p.card || null;
                return (
                  <div key={p.socketId} className={`p-2 rounded border ${isWinner ? 'border-green-500 bg-green-200 dark:bg-green-800' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded overflow-hidden flex-shrink-0">
                        {(() => {
                          const img = card && card.image ? card.image : null;
                          const packName = (gameState && gameState.pack) ? gameState.pack : 'cars';
                          let src = null;
                          if (img) {
                            const isUrl = String(img).startsWith('/') || String(img).startsWith('http');
                            src = isUrl ? img : (packName ? `/images/statclash/${packName}/${img}` : img);
                          } else if (card && card.title) {
                            const fname = safeFileName(card.title) + '.jpg';
                            src = `/images/statclash/${packName}/${fname}`;
                          }
                          if (src) {
                            return <img src={src} alt={(card && card.title) || ''} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/images/statclash/placeholder.jpg'; }} />;
                          }
                          return <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">No img</div>;
                        })()}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-200">{player.username}{isWinner ? ' • Winner' : ''}</div>
                        <div className="text-xs text-gray-900 dark:text-gray-200">{card ? card.title : 'No card'}</div>
                      </div>
                    </div>
                    {/* Stats list */}
                    <div className="grid grid-cols-1 gap-1">
                      {card ? Object.entries(card.stats).map(([sk, sv]) => {
                        const highlight = String(sk) === String(lr.stat);
                        return (
                          <div key={sk} className={`flex justify-between text-sm p-1 rounded ${highlight ? 'bg-blue-100 dark:bg-yellow-200 font-semibold' : 'bg-gray-100 dark:bg-gray-100'}`}>
                            <div className="text-xs text-gray-700 dark:text-gray-700">{sk}</div>
                            <div className="font-mono">{sv}</div>
                          </div>
                        );
                      }) : <div className="text-xs text-gray-500">No stats</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    ) : null;
  };

  return (
    <div className="p-4 flex flex-col items-center">
      <div className="mb-4 text-gray-600 dark:text-gray-300">Your deck: {(localDeck && localDeck.length) || 0} cards</div>
      {renderTopCard()}
      {renderPot()}
      {renderLastRound()}
      {/* Current player is shown in the GameRoom heading; avoid duplicating it here */}
    </div>
  );
}

StatClashGame.Guide = function StatClashGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Stat Clash</h3>
      <p>Choose a stat from your top card. The best stat wins the round and collects cards.</p>
      <p>Game ends when one player has all the cards, or after a set number of rounds.</p>
      <p>In case of a tie, the pot carries over to the next round.</p>
      <p>Good luck and may the best stats win!</p>
    </div>
  );
};
